import React, { useEffect, useMemo, useState } from "react";
import { TableLoadingSkeleton } from "src/shared/components/tables/table-loading-skeleton";
import { NoData } from "src/shared/components/tables/no-data";
import {
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  TablePagination,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import moment from "moment";
import StatusChip from "src/shared/components/status-chip";
import { LoadingButton } from "@mui/lab";
import NoFilterSelected from "src/shared/components/no-filters-selected";
import TransactionsDetailsDrawer from "../../components/transaction-details-popup";
import { downloadFile } from "src/utils";
import { useGetLocationsList } from "src/api/locations";
import {
  useExportTransactionsList,
  useGetTransactionsList,
} from "src/api/transactions";
import { paymentsTypes, statusTypes } from "../../constants";
import { useGetCitiesList } from "src/api/general";
import { ICity } from "src/types/generic";
import { DatePicker } from "@mui/x-date-pickers";
import { notificationsState } from "src/store/notifications";
import { useRecoilState } from "recoil";
import CustomAutocomplete from "src/shared/components/custom-auto-complete";
import InvoicePopup from "../../components/invoice-popup";
import { serviceLabels } from "src/constants/general";

const allOption = "all";

const tableHeadText = [
  "Ticket Num",
  "Date",
  "Location Name",
  "Service",
  "Service type",
  "Check-out",
  "Payment Status",
  "Tax Invoice",
  "Status",
];

const minDate = new Date("2024-01-01T00:00:00");
const maxDate = moment().add(1, "year").endOf("year").toDate();

const TransactionsTable = () => {
  const [openStartDate, setOpenStartDate] = useState<boolean>(false);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [openEndDate, setOpenEndDate] = useState<boolean>(false);
  const [notifications, setNotifications] = useRecoilState(notificationsState);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchBy, setSearchBy] = useState<string>("");
  const [primSearchBy, setPrimSearchBy] = useState<string>("");
  const [location, setLocation] = useState<any>(null);
  const [selectedService, setSelectedService] = useState<string>("all");
  const [totalCount, setTotalCount] = useState<number>(0);
  const [selectedPayment, setSelectedPayment] = useState<string>("all");
  const [selectedStatus, setSelectedStatus] = useState<string>("all");
  const [open, setOpen] = useState(false);
  const [clickedRow, setClickedRow] = useState();
  const [lastDocumentIds, setLastDocumentIds] = useState<string[]>([]);
  const [selectedCity, setSelectedCity] = useState<ICity>();

  const fromDate = moment(startDate).startOf("day").valueOf();

  const toDate = moment(endDate).endOf("day").valueOf();

  const filters = {
    type: "active",
    location: location?.id,
    ...(selectedCity && { cityId: selectedCity.id }),
    ...(startDate && { from: `${fromDate}` }),
    ...(endDate && { to: `${toDate}` }),
    ...(searchBy && { search: searchBy }),
    ...(primSearchBy && { customerNumber: primSearchBy }),
    ...(selectedService && { service: `${selectedService}` }),
    ...(selectedPayment !== "all" && { paymentTypes: [selectedPayment] }),
    ...(selectedStatus !== "all" && { status: `${selectedStatus}` }),
  };

  // APIS
  const { data: locationsData } = useGetLocationsList();
  const {
    data: transactionsData,
    isLoading,
    status,
    error,
  } = useGetTransactionsList({
    ...filters,
    limit: `${rowsPerPage}`,
    ...(!!lastDocumentIds[lastDocumentIds.length - 1] && {
      startAfter: lastDocumentIds[lastDocumentIds.length - 1],
    }),
  });
  const { data: citiesData } = useGetCitiesList({
    type: "active",
  });

  const { refetch: refetchExport, isFetching: isFetchingExport } =
    useExportTransactionsList({ ...filters });

  const handleChangePage = (event: unknown, newPage: number) => {
    if (newPage > page) {
      setLastDocumentIds((prev) => [
        ...prev,
        transactionsData?.data?.[transactionsData?.data?.length - 1]?.id,
      ]);
    } else {
      setLastDocumentIds((prev) => prev.slice(0, newPage));
    }
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    setLastDocumentIds([]);
  };

  const handleSearch = (searchedVal: string) => {
    setPage(0);
    setSearchBy(searchedVal);
  };

  const handleSearchByCustomerNumber = (searchedVal: string) => {
    setPage(0);
    setPrimSearchBy(searchedVal);
  };

  const clearSearch = () => {
    if (searchBy !== "") {
      handleSearch("");
    }
  };

  const clearCustomerNumSearch = () => {
    if (primSearchBy !== "") {
      handleSearchByCustomerNumber("");
    }
  };

  //Filters
  const handleChangeServiceType = (e: SelectChangeEvent) => {
    const value = e.target.value;
    setSelectedService(value);
  };

  const handleChangePayment = (e: SelectChangeEvent) => {
    const value = e.target.value;
    setSelectedPayment(value);
  };

  const handleChangeStatusType = (e: SelectChangeEvent) => {
    const value = e.target.value;
    setSelectedStatus(value);
  };

  const handleChangeLocation = (event: React.SyntheticEvent, newValue: any) => {
    setLocation(newValue);
  };

  const handleChangeCity = (event: React.SyntheticEvent, newValue: any) => {
    setSelectedCity(newValue);
  };

  const handleExport = () => {
    refetchExport().then((res) => {
      if (res.status === "success" && res.data) {
        const utf8CSVData = `\uFEFF${res.data}`;
        downloadFile(utf8CSVData, "text/csv", "transactions");
      }
    });
  };

  const handleCloseDrawer = () => {
    setOpen(false);
    setClickedRow(undefined);
  };

  const emptyDataArr = transactionsData?.data.length === 0;

  useEffect(() => {
    setPage(0);
    setLastDocumentIds([]);
  }, [
    location,
    startDate,
    endDate,
    selectedService,
    selectedPayment,
    searchBy,
  ]);

  useEffect(() => {
    if (transactionsData) {
      setTotalCount(transactionsData?.totalTransactions);
    }
  }, [transactionsData]);

  const sortedLocations = useMemo(() => {
    return (
      locationsData?.locations.sort((a: any, b: any) =>
        a.nameEn?.localeCompare(b.nameEn),
      ) ?? []
    );
  }, [locationsData]);

  const filteredCities = useMemo(() => {
    if (!citiesData?.data) return [];

    if (!location) {
      return citiesData.data;
    }

    const availableLocations = location ? [location] : sortedLocations;

    const availableLocationsCitiesIds = availableLocations.map(
      (loc: any) => loc.cityId,
    );

    return citiesData.data.filter((city) =>
      availableLocationsCitiesIds.includes(city.id),
    );
  }, [location, citiesData, sortedLocations]);

  useEffect(() => {
    setSelectedCity(undefined);
  }, [location]);

  const maxAllowedDays = 31;
  useEffect(() => {
    if (!fromDate || !toDate) return;

    const days = moment(toDate).diff(moment(fromDate), "days", true);

    if (days > maxAllowedDays) {
      setNotifications((prev) => [
        ...prev,
        { type: "error", message: "Max date range is 31 days" },
      ]);
    }
  }, [fromDate, toDate]);

  // error handling
  useEffect(() => {
    if (status === "error") {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message: error?.data?.message ?? "Oops! Something went wrong.",
        },
      ]);
    }
  }, [status]);

  return (
    <>
      <Grid container spacing={1} mb={3}>
        {/* search by customer number */}
        <Grid item xs={12} md={2}>
          <TextField
            fullWidth
            size="small"
            label="Customer Number"
            value={primSearchBy}
            onChange={(event) =>
              handleSearchByCustomerNumber(event.target.value)
            }
            InputProps={{
              endAdornment: (
                <InputAdornment
                  position="end"
                  sx={{ opacity: !primSearchBy ? 0 : 1 }}
                >
                  <IconButton size="small" onClick={clearCustomerNumSearch}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            InputLabelProps={{
              shrink: true,
            }}
          />
        </Grid>
        {/* locations */}
        <Grid item xs={12} md={2}>
          <CustomAutocomplete
            size="small"
            fullWidth
            disablePortal
            id="combo-box-demo"
            options={sortedLocations}
            getOptionLabel={(option: any) => option.nameEn}
            getOptionKey={(option: any) => option.id}
            value={location}
            onChange={handleChangeLocation}
            renderInput={(params: any) => (
              <TextField fullWidth {...params} label={"Location"} />
            )}
          />
        </Grid>
        {/* city */}
        <Grid item xs={12} md={1.8}>
          <CustomAutocomplete
            key={selectedCity?.id ?? "city-autocomplete"}
            size="small"
            disablePortal
            id="combo-box-demo"
            options={filteredCities}
            getOptionLabel={(option) => option.nameEn}
            getOptionKey={(option) => `${option.id}`}
            value={selectedCity}
            onChange={handleChangeCity}
            renderInput={(params: any) => (
              <TextField fullWidth {...params} label={"City"} />
            )}
          />
        </Grid>
        <Grid item xs={12} md={6} display={"flex"} direction={"row"} gap={1}>
          {/* start date */}
          <DatePicker
            label="Start Date"
            slotProps={{
              textField: {
                size: "small",
                onClick: () => setOpenStartDate(true),
                sx: { width: "126px" },
                inputProps: { readOnly: true },
              },
            }}
            format={"dd/MM/yyyy"}
            value={startDate}
            onChange={(newValue) => setStartDate(newValue)}
            monthsPerRow={4}
            name="start date"
            disableOpenPicker
            open={openStartDate}
            onClose={() => setOpenStartDate(false)}
            minDate={minDate}
            maxDate={maxDate}
          />
          {/* end date */}
          <DatePicker
            label="End Date"
            slotProps={{
              textField: {
                size: "small",
                onClick: () => setOpenEndDate(true),
                sx: { width: "126px" },
                inputProps: { readOnly: true },
              },
            }}
            format={"dd/MM/yyyy"}
            value={endDate}
            onChange={(newValue) => setEndDate(newValue)}
            name="end date"
            disableOpenPicker
            open={openEndDate}
            onClose={() => setOpenEndDate(false)}
            minDate={minDate}
            maxDate={maxDate}
          />
        </Grid>
      </Grid>
      {primSearchBy || (startDate && endDate && (location || selectedCity)) ? (
        <Paper
          sx={{
            bgcolor: "#1A1A1A",
            boxShadow: "0px 14px 44px 0px rgba(0, 0, 0, 0.35)",
            padding: { xs: "15px", md: "30px" },
          }}
        >
          <Grid container spacing={{ xs: 1.4, sm: 1 }} mb={3}>
            {/* search */}
            <Grid item xs={12} md={2.5}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search"
                value={searchBy}
                onChange={(event) => handleSearch(event.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment
                      position="end"
                      sx={{ opacity: !searchBy ? 0 : 1 }}
                    >
                      <IconButton size="small" onClick={clearSearch}>
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            {/* payment type */}
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel id="demo-simple-select-label">
                  Payment Type
                </InputLabel>
                <Select
                  labelId="demo-simple-select-label"
                  id="demo-simple-select"
                  value={selectedPayment}
                  label="Payment Type"
                  onChange={handleChangePayment}
                  fullWidth
                  sx={{ textTransform: "capitalize" }}
                >
                  <MenuItem value={"all"}>All</MenuItem>
                  {paymentsTypes.map((payment) => (
                    <MenuItem
                      key={payment}
                      value={payment}
                      sx={{ textTransform: "capitalize" }}
                    >
                      {payment}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {/* status */}
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel id="demo-simple-select-label">Status</InputLabel>
                <Select
                  labelId="demo-simple-select-label"
                  id="demo-simple-select"
                  value={selectedStatus}
                  label="Status"
                  onChange={handleChangeStatusType}
                  fullWidth
                >
                  <MenuItem value={"all"}>All</MenuItem>;
                  {statusTypes.map((type) => {
                    return (
                      <MenuItem key={type.value} value={type.value}>
                        {type.label}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Grid>
            {/* Service */}
            <Grid item xs={12} md={2.3}>
              <FormControl fullWidth size="small">
                <InputLabel id="demo-simple-select-label">Service</InputLabel>
                <Select
                  labelId="demo-simple-select-label"
                  id="demo-simple-select"
                  value={selectedService}
                  label="Service"
                  onChange={handleChangeServiceType}
                  fullWidth
                >
                  <MenuItem value={"all"}>All</MenuItem>
                  <MenuItem value={"valet"}>Valet</MenuItem>
                  <MenuItem value={"selfParking"}>Self Parking</MenuItem>
                  <MenuItem value={"golfCar"}>Golf Cart</MenuItem>
                  <MenuItem value={"partialCarWash"}>Partial Car Wash</MenuItem>
                  <MenuItem value={"fullCarWash"}>Complete Car Wash</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {!emptyDataArr ? (
              <Grid
                item
                xs={12}
                md={2}
                display={"flex"}
                justifyContent={"flex-end"}
              >
                <LoadingButton
                  variant="contained"
                  sx={{ gap: 0.5 }}
                  onClick={handleExport}
                  loading={isFetchingExport}
                >
                  Export
                </LoadingButton>
              </Grid>
            ) : null}
          </Grid>
          <TableContainer>
            <Table sx={{ minWidth: 650 }} aria-label="simple table">
              <TableHead>
                <TableRow>
                  {tableHeadText.map((item) => (
                    <TableCell
                      key={item}
                      sx={{
                        fontWeight: 600,
                        color: "#A1A5B7",
                      }}
                    >
                      {item}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              {isLoading ? (
                <TableLoadingSkeleton
                  rowsLength={rowsPerPage}
                  cellsLength={9}
                />
              ) : (
                <TableBody>
                  {transactionsData?.data?.map((row: any) => {
                    const { paymentStatus } = row;
                    return (
                      <TableRow
                        key={row.id}
                        hover
                        onClick={() => {
                          setOpen(true);
                          setClickedRow(row);
                        }}
                        sx={{
                          cursor: "pointer",
                          "&:last-child td, &:last-child th": {
                            border: 0,
                          },
                        }}
                      >
                        <TableCell>{row.ticketNumber}</TableCell>
                        <TableCell>
                          {row?.checkInTime || row?.arrivalTime
                            ? moment(
                                row?.checkInTime ?? row?.arrivalTime,
                              ).format("DD/MM/yyyy")
                            : ""}
                        </TableCell>
                        <TableCell>{row.locationName}</TableCell>
                        <TableCell>{row.service?.name}</TableCell>
                        <TableCell>
                          {serviceLabels[row.service?.type] ??
                            row.service?.type}
                        </TableCell>
                        <TableCell>
                          <StatusChip
                            color={row.checkOutTime ? "success" : "warning"}
                            label={row.checkOutTime ? "Done" : "Not Done"}
                          />
                        </TableCell>
                        <TableCell>
                          {!!paymentStatus && (
                            <StatusChip
                              color={
                                paymentStatus === "Paid"
                                  ? "success"
                                  : paymentStatus === "Free"
                                    ? "blue"
                                    : paymentStatus === "Refunded"
                                      ? "gray"
                                      : paymentStatus === "Cancelled"
                                        ? "error"
                                        : "warning" //"Pending"
                              }
                              label={paymentStatus}
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          {row.qyodInvoicePdfUrl ? (
                            <InvoicePopup
                              url={row.qyodInvoicePdfUrl}
                              invoiceId={row.qyodReferenceNumber}
                            />
                          ) : (
                            "--"
                          )}
                        </TableCell>
                        <TableCell sx={{ textTransform: "capitalize" }}>
                          {row.isNoShow ? "No Show" : row.status}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              )}
            </Table>

            {emptyDataArr && <NoData />}

            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={totalCount}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              disabled={isLoading}
            />
          </TableContainer>
          {open && !!clickedRow ? (
            <TransactionsDetailsDrawer
              transDetails={clickedRow}
              open={open}
              onClose={handleCloseDrawer}
            />
          ) : null}
        </Paper>
      ) : (
        <NoFilterSelected />
      )}
    </>
  );
};
export default TransactionsTable;
